import AntIcon from "@/components/icons/AntIcon";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Card, Col, Form, Input, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { rowGutter } from "../../../../../../common/constants";
import { Permission } from "../../../../../../common/security/enums";
import ComponentWithPermission from "../../../../../auth/components/ComponentWithPermission";
import { useIsCurrentAgentTypePlus } from "../../../../../auth/hooks";
import { DEFAULT_COMMISSIONS_LEVEL_QUERY_PARAMS } from "../../../api";
import { CommissionsSettingsLevelType } from "../../../enums";
import { CommissionsSettingsLevelFilterPageRequest } from "../../../types";
import { SelectCommissionsSettingsLevelType } from "../../SelectCommissionsSettingsLevelType";

const colSpan = 6;

interface Props {
  searchParams: CommissionsSettingsLevelFilterPageRequest;
  onCreateClick: () => void;
}

const CommissionsLevelFilterView = ({ searchParams, onCreateClick }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isCurrentAgentTypePlus = useIsCurrentAgentTypePlus();

  const [form] = Form.useForm<CommissionsSettingsLevelFilterPageRequest>();

  useEffect(() => {
    form.setFieldsValue(searchParams);
  }, [searchParams, form]);

  const onFilterSubmit = (values: CommissionsSettingsLevelFilterPageRequest) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...values,
            ...skipInitialFormValues(values, DEFAULT_COMMISSIONS_LEVEL_QUERY_PARAMS),
            pageIndex: undefined,
          },
        }),
      },
      { replace: true },
    );
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("commissions.level.titles.list")}</h2>}
      extra={
        <ComponentWithPermission permissions={[Permission.TOP_AGENT_COMMISSIONS_MANAGE]}>
          <Button type="primary" icon={<AntIcon type="plus" />} onClick={onCreateClick}>
            {t("commissions.level.actions.createLevel")}
          </Button>
        </ComponentWithPermission>
      }
    >
      <Form form={form} onFinish={onFilterSubmit} layout="vertical" name="commissionsLevelSearchForm">
        <Row gutter={rowGutter} justify="center">
          <Col span={colSpan}>
            <Form.Item
              name="keyword"
              label={t("common.search")}
              rules={[
                validations.size(
                  validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                  validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
                ),
              ]}
            >
              <Input allowClear placeholder={t("commissions.level.helpers.searchHint")} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item name="type" label={t("commissions.level.enums.type._label")} rules={[validations.none]}>
              <SelectCommissionsSettingsLevelType
                allowClear
                dataSource={
                  isCurrentAgentTypePlus
                    ? [CommissionsSettingsLevelType.CAREER]
                    : [CommissionsSettingsLevelType.BASIC, CommissionsSettingsLevelType.POINT]
                }
              />
            </Form.Item>
          </Col>

          <Col flex="110px" className="no-title-space">
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
                {t("common.filter")}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default CommissionsLevelFilterView;
